// chartTokens.ts — resolve CSS custom properties to color strings recharts can consume.
import { useEffect, useState } from 'react';

function readVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') {
    return fallback;
  }
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function chartColors() {
  return {
    accent: readVar('--accent', '#7DA88B'),
    info: readVar('--info', '#5E7BA0'),
    warning: readVar('--warning', '#C9A04D'),
    danger: readVar('--danger', '#B5564E'),
    ink: readVar('--ink-primary', '#1F3147'),
    inkSec: readVar('--ink-secondary', '#3D4D63'),
    inkTer: readVar('--ink-tertiary', '#7A8597'),
    border: readVar('--border', '#DDD5C5'),
    surface: readVar('--bg-surface', '#FFFFFF'),
    subtle: readVar('--bg-subtle', '#DDD5C5'),
  };
}

export function chartPalette(): string[] {
  const c = chartColors();
  return [c.accent, c.info, c.warning, c.danger, c.ink, c.inkSec, c.inkTer];
}

export type ChartColors = ReturnType<typeof chartColors>;

/**
 * Reactive version: re-reads CSS vars when the theme flips.
 * Watches `data-theme` attribute on `<html>` via MutationObserver.
 */
export function useChartColors(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(() => chartColors());

  useEffect(() => {
    const target = document.documentElement;
    const observer = new MutationObserver(() => {
      setColors(chartColors());
    });
    observer.observe(target, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return colors;
}

export function useChartPalette(): string[] {
  const c = useChartColors();
  return [c.accent, c.info, c.warning, c.danger, c.ink, c.inkSec, c.inkTer];
}
